import { useGetInstitutionQuery } from "@lending/hooks/queries/useGetInstitutionQuery";
import { getCreditCardNetwork } from "@lending/utils/getCreditCardNetwork";
import styles from "./CreditCardImage.module.css";
import PlaceholderCardImageSVG from "./PlaceholderCardImageSVG";

const CARD_LOGOS = {
  Mastercard: "/mastercard.svg",
  Visa: "/visa_logo_official.svg",
} as const;

const CARD_NETWORK_DISPLAY_NAMES = {
  mastercard: "Mastercard",
  visa: "Visa",
} as const;

type CreditCardImageNetwork = keyof typeof CARD_LOGOS;

const sizeSettings = {
  mastercard: { bottom: "7px", right: "7px" },
  visa: { bottom: "11.65px", right: "12px" },
  logoSize: "32px",
} as const;

const CARD_ASPECT_RATIOS = {
  Mastercard: 152 / 108,
  Visa: 1920 / 620,
} as const;

interface Props {
  network?: string;
  size?: "small" | "large";
  marginRight?: number | string;
  hideText?: boolean;
  cardName?: string;
  width?: number | string;
  height?: number | string;
}
function CreditCardImage({
  network,
  size = "small",
  marginRight,
  hideText = false,
  cardName,
  width: customWidth,
  height: customHeight,
}: Props) {
  const { institution } = useGetInstitutionQuery();

  const cardNetwork: CreditCardImageNetwork =
    CARD_NETWORK_DISPLAY_NAMES[getCreditCardNetwork(network)];
  const logo = CARD_LOGOS[cardNetwork];

  const aspectRatio = CARD_ASPECT_RATIOS[cardNetwork];
  const networkSizeSettings = sizeSettings[cardNetwork.toLowerCase() as "visa" | "mastercard"];

  const logoWidth: `${number}px` = sizeSettings.logoSize;
  const logoHeight = `${parseInt(logoWidth, 10) / aspectRatio}px`;

  const cardLogoStyle = {
    width: logoWidth,
    height: logoHeight,
    backgroundImage: `url('${logo}')`,
    ...networkSizeSettings,
  };

  const containerStyle: React.CSSProperties = {};
  if (marginRight !== undefined) containerStyle.marginRight = marginRight;
  if (customWidth !== undefined) containerStyle.width = customWidth;
  if (customHeight !== undefined) containerStyle.height = customHeight;

  return (
    <div className={`${styles.cardImage} ${styles[size]}`} style={containerStyle}>
      <PlaceholderCardImageSVG />
      {!hideText && <div className={styles.cardInstitutionName}>{institution.long_name}</div>}
      {!hideText && cardName && <div className={styles.cardName}>{cardName}</div>}
      {!hideText && <div className={styles.cardLogo} style={cardLogoStyle} />}
    </div>
  );
}

export { CreditCardImage };
